import { CONTACT_TITLES } from '../types/contact.js';

// CLIENT PORTAL — how a client is greeted, and how they are NOT.
//
// ⚠️⚠️ THE BUG: a client saw "Hi Mr". `PortalUser.displayName` is composed by
// `computeDisplayName` as `[title, firstName, lastName]` joined, and the
// greeting took the first whitespace-separated token — which, for a contact with
// a TITLE and no first name, is the honorific.
//
// ⚠️ FIXED IN THE RENDER, NOT THE DATA. A contact with a title and no first name
// is LEGITIMATE — somebody entered what they had, which is more than nothing —
// and "clean up the contacts" would be a migration that fixes today's rows and
// none of tomorrow's. The greeting has to cope.
//
// ⚠️ A greeting composed only of an honorific is worse than no greeting: "Hi Mr"
// reads as broken software, where a nameless welcome reads as ordinary. So the
// fallback chain ends in NO NAME rather than in a guess.
//
// ⚠️ It is a pure leaf so every sentence can be asserted without jsdom — this
// repo sits one jsdom file from the documented 5s-timeout cliff, and the wording
// is the part worth pinning.

/**
 * Honorifics to strip.
 *
 * ⚠️ TAKEN FROM `CONTACT_TITLES`, not restated. A title added to the contact
 * vocabulary must not become a name the portal greets somebody by, and a second
 * hand-kept list is how that would happen quietly.
 *
 * Matched case-insensitively and with an optional trailing full stop ("Mr."),
 * because a bulk import writes whatever the spreadsheet said.
 */
const HONORIFICS: ReadonlySet<string> = new Set(
  CONTACT_TITLES.map((t) => t.toLowerCase()),
);

function isHonorific(token: string): boolean {
  return HONORIFICS.has(token.replace(/\.$/, '').toLowerCase());
}

/**
 * A plausible first name for a greeting, or null.
 *
 * Order, and each step is deliberate:
 *   1. the display name with leading honorifics removed — the normal case;
 *   2. the email's LOCAL PART, but only when it reads like a name;
 *   3. null, and the caller greets nobody.
 *
 * ⚠️ THE LOCAL PART IS ONLY USED WHEN IT IS PURELY ALPHABETIC (a hyphen or
 * apostrophe allowed, as names carry those). "scott" becomes "Scott";
 * "j.smith", "info+portal" and "accounts2" fall through to no name. A mangled
 * name is worse than none, and "no name at all" is already an accepted outcome
 * here — so the bar for using an address as a name is that it looks like one.
 */
export function portalGreetingName(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string | null {
  const tokens = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < tokens.length && isHonorific(tokens[i] as string)) i += 1;
  const fromName = tokens[i];
  if (fromName) return fromName;

  const local = (email ?? '').trim().split('@')[0] ?? '';
  if (/^[\p{L}][\p{L}'-]*$/u.test(local)) {
    return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return null;
}

/**
 * The heading a signed-in client sees.
 *
 * ⚠️ "Welcome" rather than "Welcome back": a first sign-in is exactly when this
 * fallback fires most often (nobody has typed a name yet), and greeting a
 * first-time visitor with "back" is the small wrongness that makes software feel
 * unattended. It is also warmer than the "Hi there" it replaces, which is what
 * the brief asked for.
 */
export function portalGreeting(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string {
  const name = portalGreetingName(displayName, email);
  return name ? `Hi ${name}` : 'Welcome';
}
